import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Download, Monitor, Smartphone } from 'lucide-react';
import { AppChrome } from '../components/alt/AppChrome';
import { SharedScreen, WorkflowsScreen, PulsesScreen, SkillsScreen } from '../components/alt/PlatformPillar';
import { ConnectionsScreen } from '../components/alt/ConnectedAppsPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { MOSAIC_CARDS } from '../components/alt/AccelerationMosaic';
import { ConnectedAppsTable } from './Alt';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal } from './CompanyBrain';
import { SURFACE_PILLS, TOOL_PILLS, CRM_PILLS } from '../components/alt/SurfacePill';
import { SurfacePills } from '../components/exports/SurfacePills';
import { StillProvider } from '../components/alt/stillContext';
import { SharedAccessPanel } from '../components/exports/SharedAccessPanel';
import { SkillShelf } from '../components/exports/SkillShelf';
import { TimeSavedPanel } from '../components/exports/TimeSavedPanel';
import { GigaInChatGPT } from '../components/exports/GigaInChatGPT';
import { useExportFrames } from '../components/exports/useExportFrames';
import { AgentBuilderCard, ApprovalCard, ConnectionsCard, SharedAgentsCard, TemplateWall } from '../components/aiagents/agentArtefacts';
import { TrackWorkspace } from '../components/gigachat/TrackWorkspace';
import { GigaChatScreen } from '../components/exports/GigaChatScreen';

/**
 * Export surface for the product screens used in the Workspace section of the
 * home page.
 *
 * These are vectorised, not screenshotted: the DOM is walked and re-emitted as
 * SVG primitives, so what lands in Figma is real rectangles, real lines and
 * real text layers you can retype and restyle — not a flat picture.
 */

type Screen = {
  id: string;
  name: string;
  note: string;
  section: string;
  /** Product screens sit in app chrome; diagrams and panels stand alone. */
  chrome?: boolean;
  /** Screen already carries its own inner padding. */
  selfPadded?: boolean;
  active?: string;
  /** Screens that are the whole view — no app nav beside them. */
  noSidebar?: boolean;
  /** Wider canvas for the full-bleed diagrams. */
  wide?: boolean;
  /**
   * Artboard width in px, for frames that were composed narrower than the
   * default 900. A card designed as a third of a twelve-column board stretches
   * badly at full width — its lists go from two lines to one and the visual
   * stops matching what ships on the page.
   */
  maxWidth?: number;
  /**
   * PORTRAIT SUPPORT. Most of these assets end up as an overlay on a vertical
   * video, where a 900px-wide frame has to be shrunk until its labels are
   * unreadable. A frame marked `portrait` can genuinely RE-COMPOSE for a
   * narrow artboard — its rows wrap and its grids collapse — so it is exported
   * at phone width with type still at full size, rather than scaled down.
   *
   * Screens reused from the site are not marked: they carry `md:` breakpoints
   * tied to the viewport, not to their container, so narrowing the artboard
   * would squash them rather than reflow them. Those stay landscape.
   */
  portrait?: boolean;
  /**
   * WHEN TO REACH FOR THIS ONE. `note` says what is in the frame; this says
   * what it is FOR. Written for the person scrolling this page looking for
   * something to drop into a video, a deck or a doc — so it names the job
   * ("showing Giga answering in a channel"), not the contents.
   *
   * Kept to a sentence or two. Anyone who needs more than that is looking at
   * the wrong frame, and the answer is a better frame rather than longer copy.
   */
  use: string;
  render: (portrait: boolean) => React.ReactNode;
};
/** Phone-ish artboard width for portrait mode, before the frame's own padding. */
const PORTRAIT_WIDTH = 400;
/**
 * What each group of frames is for, in one line. The section rule already
 * names the pillar; this says which pitch the frames beneath it serve, so you
 * can skip three sections at a glance rather than reading twelve captions.
 */
const SECTION_BLURBS: Record<string, string> = {
  'Connected Apps': 'For the setup story — what Giga is plugged into, whose account it uses, and how that compares to a chatbot with no access.',
  'Company Brain': 'For the "where does it learn this?" question — company knowledge arriving from real systems rather than being typed in.',
  'AI Co Worker': 'For showing Giga working inside a tool your audience already uses, rather than in an app they would have to adopt first.',
  Workspace: 'The product itself. Reach for these when you need a real Giga screen on camera — a chat, a workflow, a schedule, a library.',
  Tracks: 'For showing how Giga keeps one body of work together — its current state, everything linked to it, and the decision trail behind it.',
  Architecture: 'For technical and buyer conversations — where Giga sits relative to your tools, your data and the models.',
  'Speed & control cards': 'The outcome claims from the home page, as UI. Reach for these when the point is what a team gets back, not how the product works.',
  'AI Agents': 'The pieces of building and running an agent, each on its own. Best used in sequence to walk through creating one.'
};
/**
 * The column width each mosaic card occupies on the home page's twelve-column
 * board, so the exported artboard matches what ships rather than a stretched
 * version of it.
 */
const MOSAIC_WIDTHS: Record<string, number> = {
  'mosaic-offload': 740,
  'mosaic-meetings': 520,
  'mosaic-answers': 420,
  'mosaic-own-data': 420,
  'mosaic-activity': 420,
  'mosaic-teams': 520,
  'mosaic-model': 740
};
/**
 * EXPORT-ONLY REPLACEMENTS FOR MARKETING CARDS.
 *
 * Two of the home page's seven board cards are arguments, not screens: a
 * headline, a paragraph, and a picture underneath. That is right on the page
 * and wrong here — a marketing claim dropped into a product video reads as an
 * ad, and the asset a teammate actually wants is the UI on its own.
 *
 * So these two frames ship purpose-built product panels instead, on the shared
 * export type scale. The other five carry real UI already and are exported
 * exactly as they appear on the home page.
 */
const MOSAIC_REPLACEMENTS: Record<string, {
  name: string;
  note: string;
  use: string;
  render: () => React.ReactNode;
}> = {
  'mosaic-meetings': {
    name: 'Time returned',
    note: 'Meetings replaced, hours back — no headline',
    use: 'Use this when the point is hours given back. It carries the number on screen, so it works as the payoff shot at the end of a demo or over a customer quote.',
    render: () => <TimeSavedPanel data-id="element-9916" data-mp="3e71lw4cwb23b" />
  },
  'mosaic-teams': {
    name: 'Built for teams',
    note: 'Group members, access levels and connections',
    use: 'Use this for anything about teams rather than individuals — onboarding a department, or answering "who can access this?" The connections available to the group are shown alongside its members and roles.',
    render: () => <SharedAccessPanel data-id="element-9917" data-mp="2umue8ecgadpz" />
  }
};
/** The export-only panels reflow properly, so they carry portrait variants. */
const PORTRAIT_READY = new Set(['mosaic-meetings', 'mosaic-teams', 'skills-shelf', 'agent-connections', 'agent-approval', 'agent-builder']);
const SCREENS: Screen[] = [
// ── Connected Apps ───────────────────────────────────────────────────────
{
  id: 'connections',
  name: 'Connections',
  note: 'Tools, accounts and the AI surfaces that reach them',
  use: 'The widest single view of what Giga is plugged into. Reach for this when someone asks "what does it connect to?" — it answers with named tools rather than a logo wall.',
  section: 'Connected Apps',
  chrome: true,
  selfPadded: true,
  noSidebar: true,
  wide: true,
  render: () => <ConnectionsScreen data-id="element-9918" data-mp="5qsv9ldorun" />
}, {
  id: 'comparison',
  name: 'Connection comparison',
  note: 'Giga vs ChatGPT vs Claude',
  use: 'The competitive slide. Use it when the audience already pays for ChatGPT or Claude and needs to see what those cannot reach.',
  section: 'Connected Apps',
  render: () => <ConnectedAppsTable data-id="element-9919" data-mp="1ghuvg3t97d5a" />
},
// ── Company Brain ────────────────────────────────────────────────────────
{
  id: 'brain-flow',
  name: 'Sources into tracks',
  note: 'Observations flowing from scans and chats',
  use: 'Use this to explain where the knowledge comes from — real systems being read, sorted into tracks. Good as a background plate for a voiceover, since it reads at a glance without being studied.',
  section: 'Company Brain',
  wide: true,
  render: () => <BrainFlowExperiment settled data-id="element-9920" data-mp="12ibo3d89o5u1" />
},
// ── AI Co Worker ─────────────────────────────────────────────────────────
{
  id: 'slack',
  name: 'Slack thread',
  note: 'Giga answering in #sales',
  use: 'Reach for this whenever you need to show a conversation happening in Slack — someone asking in a channel and Giga answering there, with no new app to open. The most reusable frame on this page.',
  section: 'AI Co Worker',
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="slack" showPlatformToggle={false} settled data-id="element-9921" data-mp="3toje6glhk7kn" />
}, {
  // Teams is its own frame rather than a toggle inside the Slack one, so it
  // can be exported and reviewed on its own terms.
  id: 'teams',
  name: 'Teams chat',
  note: 'Giga answering in Microsoft Teams',
  use: 'The same exchange for a Microsoft shop. Swap this in for the Slack frame when the audience runs Teams — showing the wrong chat tool loses the room faster than any other detail.',
  section: 'AI Co Worker',
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="teams" showPlatformToggle={false} settled data-id="element-9922" data-mp="35wvscd32zmp8" />
}, {
  id: 'claude-code',
  name: 'Claude Code terminal',
  note: 'Giga over MCP',
  use: 'For developer audiences. Use it on engineering pages, in docs, or anywhere the point is that Giga is reachable over MCP from a terminal rather than only through a UI.',
  section: 'AI Co Worker',
  render: () => <CodeTerminal data-id="element-9923" data-mp="3n829v9t43wrn" />
}, {
  // The full ChatGPT application rebuild lives on the home page. As an asset
  // it was unreadable at the size anyone views one: sidebar and composer
  // eating the frame, four turns of transcript, and the one thing that matters
  // — Giga reaching a real system — buried inside it. This is that single
  // exchange at a size you can read.
  id: 'chatgpt',
  name: 'Giga inside ChatGPT',
  note: 'One question, the Giga call, the sourced answer',
  use: 'Use this for the "keep using ChatGPT" pitch. It is deliberately one exchange, so it stays readable as a phone overlay or a single slide where a full transcript would not.',
  section: 'AI Co Worker',
  maxWidth: 620 + 80,
  portrait: true,
  render: (portrait: boolean) => <GigaInChatGPT portrait={portrait} data-id="element-9924" data-mp="yeop0ijfdnft" />
},
// ── Workspace ────────────────────────────────────────────────────────────
{
  id: 'chats',
  // "AI chats" is what the nav calls it inside the app, but as a standalone
  // asset it needs to say whose chat it is — an unlabelled chat screen in a
  // video is indistinguishable from any other assistant.
  name: 'Giga Chat',
  note: 'A sourced pipeline answer in the Brightwave workspace',
  use: 'The default "here is the product" frame. Use it to show Giga answering from real company context, handling routine work, and surfacing what needs a person.',
  section: 'Workspace',
  wide: true,
  maxWidth: 1200,
  selfPadded: true,
  render: () => <GigaChatScreen data-id="element-9925" data-mp="1ooxhwxgnq3qr" />
}, {
  id: 'groups',
  name: 'Groups & permissions',
  note: 'Every group, and the way into each',
  use: 'Use this for the access question — every group in the workspace, who is in it, and whether you get in by invite, by request, or freely. Nothing is cropped or faded here on purpose: a partial view of access answers nobody. Pairs with "Built for teams" below, which zooms into one group.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <SharedScreen data-id="element-9926" data-mp="2hbp5x4dq28ld" />
}, {
  id: 'workflows',
  name: 'Workflows',
  note: 'Monday morning update',
  use: 'Use this when the story is multi-step work running on its own — the steps, what each one touches, and the record beside the name: owner, who can run it, last run, and Run now sitting with the title rather than buried under the steps.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <WorkflowsScreen data-id="element-9927" data-mp="1yenxf9kylqco" />
}, {
  id: 'pulses',
  name: 'Pulses',
  note: 'One running, three scheduled',
  use: 'Use this for the always-on angle — work on a schedule that arrives without being asked. The third row is mid-run, with its step bar showing where it has got to, and every row carries how many times it has fired and since when. That record is the argument: this has been quietly doing the job for months.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <PulsesScreen data-id="element-9928" data-mp="1apbf9k7nm7y8" />
}, {
  id: 'skills',
  name: 'Skills',
  note: 'Written by one person, run by a group',
  use: 'The real Skills view, and the frame for the handover: each row names who wrote the method, the group it was shared into, the faces of everyone who can now run it, and how many times it has been run. Use it when the point is that one person\u2019s expertise becomes the company\u2019s. For a camera-facing version, take the Skills library shelf below.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <SkillsScreen data-id="element-9929" data-mp="hrr5r5gyidzt" />
}, {
  // A presentation-ready crop of the real list hierarchy: one skill explains
  // the concept, while the remaining rows establish that it is a library.
  id: 'skills-shelf',
  name: 'Skills library',
  note: 'One featured method and a compact reusable library',
  use: 'Use this in a video or slide to explain Skills quickly: one method shows what gets captured, while the compact rows show authorship, team access, and repeated use.',
  section: 'Workspace',
  maxWidth: 900 + 80,
  portrait: true,
  render: (portrait: boolean) => <SkillShelf columns={portrait ? 1 : 4} data-id="element-9930" data-mp="25quvbaomx69v" />
},
// ── Tracks ───────────────────────────────────────────────────────────────
// One coherent lead-to-pipeline story across all six locked workspace states.
// The same facts appear in Chat, Overview, Linked, Activity, Pulses and Drive,
// so a marketing sequence can cut between tabs without changing the story.
{
  id: 'track-chat',
  name: 'Track chat',
  note: 'A pipeline question answered from four connected systems',
  use: 'Use this to introduce Tracks through a familiar action: ask what changed, then show Giga checking CRM, email, routing rules and inbound activity before returning risks and next actions.',
  section: 'Tracks',
  wide: true,
  maxWidth: 1200,
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="chat" exportMode data-id="element-9931" data-mp="2ti0bxs2eg2l8" />
}, {
  id: 'track-overview',
  name: 'Track overview',
  note: 'Live pipeline state, risks, recent chats and source context',
  use: 'Use this to show that a Track briefs the whole team: 18 enriched leads, four routing exceptions and $184k in stalled pipeline are visible without reconstructing the work.',
  section: 'Tracks',
  wide: true,
  maxWidth: 1200,
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="overview" exportMode data-id="element-9932" data-mp="30zouo6rpp2hp" />
}, {
  id: 'track-linked',
  name: 'Track resources',
  note: 'The tools, rules, prompts and conversations behind the result',
  use: 'Use this for scoped context. Close, Gmail, Clearbit, territory rules, Slack channels and the revenue playbook are visibly attached to this work—not hidden in a global knowledge base.',
  section: 'Tracks',
  wide: true,
  maxWidth: 1200,
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="linked" exportMode data-id="element-9933" data-mp="2n05nbg6q5v6g" />
}, {
  id: 'track-activity',
  name: 'Track activity',
  note: 'A chronological trail from lead enrichment to team decisions',
  use: 'Use this for traceability: the frame shows Giga routing leads, surfacing stalled pipeline and linking source context alongside human approvals and territory-rule changes.',
  section: 'Tracks',
  wide: true,
  maxWidth: 1200,
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="activity" exportMode data-id="element-9934" data-mp="9tz6ognu696x" />
}, {
  id: 'track-pulses',
  name: 'Track pulses',
  note: 'Routing exceptions and pipeline risks monitored automatically',
  use: 'Use this for the always-on story. Daily routing checks, Monday briefs and stalled-opportunity monitoring keep the Track current and bring exceptions to the team before someone asks.',
  section: 'Tracks',
  wide: true,
  maxWidth: 1200,
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="pulses" exportMode data-id="element-9935" data-mp="orhv1hwlqkdu" />
}, {
  id: 'track-drive',
  name: 'Track files',
  note: 'Briefs, exception reports and approved source documents',
  use: 'Use this to show that Giga produces durable work—not just answers. Generated pipeline briefs and routing reports stay beside the playbooks and mappings used to create them.',
  section: 'Tracks',
  wide: true,
  maxWidth: 1200,
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="drive" exportMode data-id="element-9936" data-mp="344sdp8jbpi2s" />
},
// ── Architecture ─────────────────────────────────────────────────────────
{
  id: 'architecture',
  name: 'Architecture',
  note: 'Ways to use Giga',
  use: 'For technical buyers and security reviews. Use it to show where Giga sits between your tools, your data and the models — the frame to bring to an IT conversation.',
  section: 'Architecture',
  wide: true,
  render: () => <ArchitectureWidget data-id="element-9937" data-mp="3rve7pvwht1ts" />
},
// ── Speed & control board ────────────────────────────────────────────────
// The seven panels from "The fastest version of your company is already
// possible", each on its own artboard. They are pulled from the section's own
// exported registry rather than rebuilt here, so a change to a panel on the
// home page lands in the export automatically. Narrow artboards: these are
// cards, not full screens, and exporting them at 900px would stretch a
// four-column layout that was composed at roughly half that.
...MOSAIC_CARDS.map(card => {
  const swap = MOSAIC_REPLACEMENTS[card.id];
  return {
    id: card.id,
    name: swap?.name ?? card.name,
    note: swap?.note ?? card.note,
    // The five untouched cards keep marketing copy inside the frame, so their
    // guidance says so plainly rather than pretending they are product shots.
    use: swap?.use ?? `The card as it ships on the home page, headline and all. Use it when you want the claim itself on screen — for a product screenshot without marketing copy, reach for a Workspace frame instead.`,
    section: 'Speed & control cards',
    // Each artboard is the card's own column width on the board plus the
    // frame's 40px of padding on each side. The replacements are panels rather
    // than board cards, so they get a little more room for their rows.
    maxWidth: (swap ? 620 : MOSAIC_WIDTHS[card.id] ?? 560) + 80,
    portrait: PORTRAIT_READY.has(card.id),
    render: swap?.render ?? card.render
  };
}),
// ── AI Agents ────────────────────────────────────────────────────────────
// The five artefacts from the AI Agents page, exported BARE — without the
// mosaic panel, tint and copy that frame them on the page. Those are page
// composition; what a designer needs from an export is the product object on
// its own, at the size it was drawn rather than the reduced size it appears at
// inside a marketing card.
{
  id: 'agent-builder',
  name: 'Agent builder',
  note: 'Brief, model, schedule and connections',
  use: 'Step one of the agent story: someone describing the job in plain words. Use it for "how do you make one?" — it is already phone-width, so it drops straight onto a vertical video.',
  section: 'AI Agents',
  // Its own width plus the settings panel it overlaps on the right.
  maxWidth: 340 + 80,
  // Already phone-width by design, so portrait mode leaves it alone.
  portrait: true,
  render: () => <AgentBuilderCard data-id="element-9938" data-mp="2c527atgzare" />
}, {
  id: 'agent-templates',
  name: 'Template shelf',
  note: 'Four pre-built agents, one lit',
  use: 'For the "you do not start from nothing" beat. Use it early in a demo, before the builder, to show there is a shelf to pick from.',
  section: 'AI Agents',
  // The wide canvas the shelf is composed on — on the page it is reduced as a
  // whole object, but the export wants it at full size.
  maxWidth: 900 + 80,
  render: () => <TemplateWall data-id="element-9939" data-mp="3kkw1lwwpgd7l" />
}, {
  id: 'agent-connections',
  name: 'Connections panel',
  note: 'Two connected, three waiting',
  use: 'A small frame for the moment an agent is given access to a tool. Use it as an inset or overlay rather than a full slide — it is deliberately compact.',
  section: 'AI Agents',
  maxWidth: 230 + 80,
  portrait: true,
  render: () => <ConnectionsCard data-id="element-9940" data-mp="31td0jvm8r987" />
}, {
  id: 'agent-approval',
  name: 'Approval prompt',
  note: 'Decline, always allow, approve',
  use: 'The frame for every trust and safety conversation — an agent stopping to ask before it acts. Reach for this whenever someone worries about AI doing something unattended.',
  section: 'AI Agents',
  maxWidth: 300 + 80,
  portrait: true,
  render: () => <ApprovalCard data-id="element-9941" data-mp="2s3nxre2y8mg9" />
}, {
  id: 'agent-shared',
  name: 'Shared agents',
  note: 'Shared with you · 12 of 24',
  use: 'For the spread of adoption — one person builds an agent, the team inherits it. Use it as the closing frame of the agent sequence.',
  section: 'AI Agents',
  maxWidth: 400 + 80,
  render: () => <SharedAgentsCard data-id="element-9942" data-mp="2w59fpducbqxb" />
}];
export function ExportDashboards() {
  const {
    register,
    busy,
    copied,
    warm,
    copy,
    save,
    savePng,
    saveAllPng
  } = useExportFrames();
  /**
   * ORIENTATION IS PER FRAME, NOT PER PAGE.
   *
   * A single page-wide switch was the wrong model: you almost never want every
   * frame in one orientation, you want THIS screen vertical for a short and
   * that diagram wide for a slide. So each frame owns its own state, and the
   * pair of buttons in the header is a "set them all" shortcut rather than the
   * source of truth.
   *
   * `undefined` for a frame means "follow the page default".
   */
  const [pageDefault, setPageDefault] = useState(false);
  const [orientation, setOrientation] = useState<Record<string, boolean>>({});
  const isPortrait = (s: Screen) => !!s.portrait && (orientation[s.id] ?? pageDefault);
  const setAll = (portrait: boolean) => {
    setPageDefault(portrait);
    // Clear the per-frame overrides, otherwise "set all" would visibly skip
    // the frames someone had already switched by hand.
    setOrientation({});
  };
  return <main className="min-h-screen w-full bg-[#F4F1EA] pb-24" data-id="element-9943" data-mp="ogm86ake80th">
      <header className="border-b border-[#E7E0D4] bg-[#FAF8F4]" data-id="element-9944" data-mp="og4cbf1n7om3">
        <div className="mx-auto max-w-[1240px] px-6 py-10" data-id="element-9945" data-mp="2la7ifxuc08hn">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-9946" data-mp="2q893upf8cjwa">
            Export
          </p>
          <h1 className="mt-3 font-title text-3xl font-semibold tracking-tight text-[#2B211A] md:text-[40px]" data-id="element-9947" data-mp="17dea6dlyej7g">
            Product screen library
          </h1>
          <p className="mt-3 max-w-[64ch] text-[15.5px] leading-relaxed text-[#6B5D52]" data-id="element-9948" data-mp="12kl4nninv6ub">
            Every product screen, diagram and agent artefact used across the
            site, ready to take. Download a PNG for a video, a deck or a doc, or
            copy one into Figma as editable layers — real rectangles and real
            text you can retype, not a screenshot. Every frame is held on its
            finished state, so nothing arrives mid-animation or half-loaded.
          </p>

          <ol className="mt-6 max-w-[64ch] space-y-2 rounded-xl border border-[#E7E0D4] bg-white px-5 py-4 text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-9949" data-mp="1hd56mku2r8a6">
            <li data-id="element-9950" data-mp="5x6q5vwkhjyx">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9951" data-mp="5xqb7lhikoh6">1.</strong> Keep
              this window at desktop width — the app sidebar only renders above
              1024px, and the export captures what is actually on screen.
            </li>
            <li data-id="element-9952" data-mp="1tk8e64gk1smh">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9953" data-mp="1tkqhfl1mx73w">2.</strong> Hit
              Copy, then paste directly onto a Figma canvas.
            </li>
            <li data-id="element-9954" data-mp="1fqza8jbdlzbc">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9955" data-mp="1ks6h8g5p2ugy">3.</strong>{' '}
              Install Inter in Figma so the type matches — headings are Inter
              Display (the same family at optical size 32) set at weight 560,
              not bold. Everything arrives as live text layers.
            </li>
          </ol>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-9956" data-mp="1ykcyk5kl37hz">
            <button type="button" onClick={() => saveAllPng(SCREENS.map(s => s.id))} disabled={busy !== null} className="inline-flex items-center gap-2 rounded-lg bg-[#EA580C] px-4 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#C2410C] disabled:opacity-60" data-id="element-9957" data-mp="23io4443uivue">
              <Download size={14} data-id="element-9958" data-mp="s1eppvzkqtzy" />
              {busy ? 'Exporting…' : `Download all ${SCREENS.length} as PNG`}
            </button>

            {/* The vertical plates live on their own page. Someone cutting a
             short has a single job, and making them scroll past twenty
             landscape frames to reach six plates was the wrong order. */}
            <Link to="/app-screen-mockups/creator-pack" className="inline-flex items-center gap-2 rounded-lg border border-[#D8CFC2] bg-white px-3.5 py-2.5 text-[13px] font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-9959" data-mp="omv9vky97xsn">
              <Smartphone size={14} data-id="element-9960" data-mp="3sxp5oqombtko" />
              Creator pack · 9:16 for video
            </Link>

            {/* Frames that can re-compose switch to a phone-width artboard
             with type at full size; the rest stay landscape and say so,
             because squashing a screen whose breakpoints watch the viewport
             would only mislead. */}
            <div role="group" aria-label="Set every artboard orientation" className="inline-flex items-center rounded-lg border border-[#D8CFC2] bg-white p-1" data-id="element-9961" data-mp="39b15byi08sfc">
              {([[false, 'All landscape', Monitor], [true, 'All mobile', Smartphone]] as const).map(([value, label, Icon]) => <button key={label} type="button" onClick={() => setAll(value)} aria-pressed={pageDefault === value} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] font-bold transition-colors ${pageDefault === value ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-9962" data-mp="1mxxvcrsqswb9">
                  <Icon size={13} data-id="element-9963" data-mp="z9ob3ub6ggm8" />
                  {label}
                </button>)}
            </div>
          </div>

          <p className="mt-4 max-w-[64ch] text-[12.5px] leading-relaxed text-neutral-500" data-id="element-9964" data-mp="146ohk6bxvldv">
            Brand logos are downloaded and embedded into the file, so they
            survive the paste — that is what the short pause after pressing Copy
            is. Drop shadows are dropped rather than faked. The animated screens
            are held on their finished state here — the full Slack thread, the
            complete ChatGPT transcript, every observation landed — so the file
            is never captured mid-animation.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-[1360px] space-y-14 px-6 pt-14" data-id="element-9965" data-mp="3vdfg7zsx4z5u">
        <SurfacePills heading="Surface lockups" title="Platform mark and name" note="Transparent artboard — the capsule is translucent, so whatever sits behind it still shows through. PNG is rasterised at 4× with the real type embedded; SVG keeps the word as live text." items={SURFACE_PILLS} data-id="element-9966" data-mp="3qfmcrej02j99" />
        <SurfacePills heading="Tool lockups" title="Connected tool mark and name" note="The same lockup for every tool named across the site. Marks come from the shared brand registry, so they match the connections map exactly." items={TOOL_PILLS} data-id="element-9967" data-mp="3cfbg1uv0eag6" />
        <SurfacePills heading="CRM lockups" title="CRM mark and name" note="The CRMs a buyer is most likely to already run. Useful for integration pages and campaign creative that names the CRM the reader uses." items={CRM_PILLS} data-id="element-9968" data-mp="1anq1rnw4nfdo" />
      </div>

      <div className="mx-auto max-w-[1360px] space-y-14 px-6 pt-14" data-id="element-9969" data-mp="jimc9m2bhzwk">
        {SCREENS.map((s, i) => {
        const copiedNow = copied === s.id;
        const busyNow = busy === s.id;
        const newSection = i === 0 || SCREENS[i - 1].section !== s.section;
        // Portrait applies only where the frame can genuinely re-compose.
        const asPortrait = isPortrait(s);
        return <section key={s.id} data-id="element-9970" data-mp="3s11z5x7szku9" data-mpi={i}>
              {/* Grouped by the pillar the screen belongs to, so the page
               matches the order you scroll past on the home page. */}
              {newSection && <div className="mb-5 border-b border-[#E7E0D4] pb-3" data-id="element-9971" data-mp="3i2ukjl0zlevf">
                  <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-9972" data-mp="3n04hrmumautt">
                    {s.section}
                  </p>
                  {/* What this group of frames is for. One line, so you can
                   skip a whole section without reading its captions. */}
                  {SECTION_BLURBS[s.section] && <p className="mt-1.5 max-w-[76ch] text-[13.5px] leading-relaxed text-[#6B5D52]" data-id="element-9973" data-mp="30r77uulr81i3">
                      {SECTION_BLURBS[s.section]}
                    </p>}
                </div>}
              {/* Top-aligned now the caption has made the title block taller —
               end-aligned buttons would drift down beside two lines of copy. */}
              <div className="mb-4 flex flex-wrap items-start justify-between gap-4" data-id="element-9974" data-mp="3ofgrbpvhwd62">
                <div data-id="element-9975" data-mp="3jgnbhqsqka2q">
                  <h2 className="font-title flex flex-wrap items-center gap-2 text-[22px] font-semibold tracking-tight text-[#2B211A]" data-id="element-9976" data-mp="3jhf5wyalk1rf">
                    {s.name}
                    {/* Frames that cannot re-compose say so, rather than
                     silently ignoring the switch. */}
                    {!s.portrait && <span className="rounded-full bg-[#F1EAE0] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#8A7B6D]" data-id="element-9977" data-mp="2430wzpy8ocvd">
                        Landscape only
                      </span>}
                  </h2>
                  <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-9978" data-mp="3oshovn651u22">
                    {s.note} · giga-{s.id}.svg
                  </p>
                  {/* When to reach for this frame. Sits under the name rather
                   than beside the buttons, so it is read while deciding —
                   not after you have already pressed Download. */}
                  <p className="mt-2 max-w-[62ch] text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-9979" data-mp="3ayqbg3padq12">
                    {s.use}
                  </p>
                </div>
                <div className="relative z-10 flex flex-wrap items-center gap-2" onPointerEnter={() => warm(s.id)} onFocus={() => warm(s.id)} data-id="element-9980" data-mp="313oq7iy4t83r">
                  {/* This frame's own orientation. Sits with its export
                   buttons because it changes what those buttons produce. */}
                  {s.portrait && <div role="group" aria-label={`${s.name} orientation`} className="mr-1 inline-flex items-center rounded-lg border border-[#D8CFC2] bg-white p-1" data-id="element-9981" data-mp="vazzkzgeuz4s">
                      {([[false, 'Desktop', Monitor], [true, 'Mobile', Smartphone]] as const).map(([value, label, Icon]) => <button key={label} type="button" onClick={() => setOrientation(o => ({
                  ...o,
                  [s.id]: value
                }))} aria-pressed={asPortrait === value} title={`${label} artboard`} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-bold transition-colors ${asPortrait === value ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-9982" data-mp="2mwlfefsoppyj">
                          <Icon size={12} data-id="element-9983" data-mp="237t1edydebfq" />
                          {label}
                        </button>)}
                    </div>}
                  <button type="button" onClick={() => savePng(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-9984" data-mp="3eoaz0hdpek3y">
                    <Download size={13} data-id="element-9985" data-mp="1jjjoltiwbkgc" />
                    {busyNow ? 'Preparing…' : 'Download PNG'}
                  </button>
                  <button type="button" onClick={() => copy(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-9986" data-mp="1flvtapdajpnx">
                    {copiedNow ? <Check size={13} className="text-emerald-600" data-id="element-9987" data-mp="vvk07092464x" /> : <Copy size={13} data-id="element-9988" data-mp="vwv0ur77gxrt" />}
                    {copiedNow ? 'Copied' : 'Copy for Figma'}
                  </button>
                  <button type="button" onClick={() => save(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-9989" data-mp="1amj3pwv1c4hn">
                    <Download size={13} data-id="element-9990" data-mp="d4sj1citvbfz" />
                    SVG
                  </button>
                </div>
              </div>

              {/* The captured node. Padding sits inside it so the exported
               artboard carries its own margin around the frame.
                `relative isolate` is load-bearing: these screens come from the
               home page, where they sit inside positioned sections. Rendered
               bare here, any decorative `absolute inset-0` layer inside them
               would resolve against the initial containing block — painting a
               viewport-sized panel over the top of the page and swallowing
               clicks on the export buttons. Positioning each frame keeps every
               overlay inside the screen it belongs to. */}
              <div ref={register(s.id)} style={{
            maxWidth: asPortrait ? PORTRAIT_WIDTH + 56 : s.maxWidth
          }} className={`relative isolate overflow-x-auto rounded-2xl ${asPortrait ? 'mx-auto p-7' : `p-10 ${s.wide ? '' : 'mx-auto max-w-[900px]'}`}`} data-id="element-9991" data-mp="33jeop62kh10n">
                {/* Everything inside a frame renders at rest. Frames are
                 usually off-screen, so any `whileInView` entrance would never
                 have fired and would export as its `initial` state — a blank
                 card, or a strike-through that was never drawn. */}
                <StillProvider data-id="element-9992" data-mp="1tlolvy53dw4v">
                  {s.chrome ? <AppChrome active={s.active} sidebar={!s.noSidebar} data-id="element-9993" data-mp="1tikdspoiag8x">
                      {s.selfPadded ? s.render(asPortrait) : <div className="px-5 py-5 md:px-7 md:py-6" data-id="element-9994" data-mp="2lbt3xq5dq72x">
                          {s.render(asPortrait)}
                        </div>}
                    </AppChrome> : s.render(asPortrait)}
                </StillProvider>
              </div>
            </section>;
      })}
      </div>
    </main>;
}